import { useState } from "react";
import {
  BarChart3,
  Calendar,
  LayoutDashboard,
  MessageSquare,
  Settings,
  Users,
  LogOut,
  Bell,
  X,
  Send,
  Loader2,
} from "lucide-react";
import { useGetIdentity, useLogout } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";

interface NavItem {
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  label: string;
  to: string;
  pattern: string;
}

const MANAGE_NAV: NavItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", to: "/", pattern: "/" },
  {
    icon: MessageSquare,
    label: "Calls",
    to: "/conversations",
    pattern: "/conversations*",
  },
  { icon: Users, label: "Patients", to: "/patients", pattern: "/patients*" },
  {
    icon: Calendar,
    label: "Appointments",
    to: "/appointments",
    pattern: "/appointments*",
  },
];

const INSIGHTS_NAV: NavItem[] = [
  {
    icon: BarChart3,
    label: "Analytics",
    to: "/analytics",
    pattern: "/analytics*",
  },
];

const SETTINGS_NAV: NavItem[] = [
  { icon: Settings, label: "Settings", to: "/settings", pattern: "/settings*" },
];

const NavLink = ({ item, active }: { item: NavItem; active: boolean }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: active ? "9px 16px 9px 14px" : "9px 16px",
        borderRadius: 8,
        margin: "1px 8px",
        marginLeft: active ? 6 : 8,
        fontSize: 14,
        fontWeight: active ? 500 : 400,
        color: active ? "#111111" : "#555555",
        textDecoration: "none",
        background: active ? "#F7F7F7" : "transparent",
        borderLeft: active ? "2px solid #000" : "none",
        transition: "background 0.12s, color 0.12s",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          (e.currentTarget as HTMLElement).style.background = "#F7F7F7";
          (e.currentTarget as HTMLElement).style.color = "#111111";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          (e.currentTarget as HTMLElement).style.background = "transparent";
          (e.currentTarget as HTMLElement).style.color = "#555555";
        }
      }}
    >
      <Icon style={{ width: 16, height: 16, flexShrink: 0 }} />
      {item.label}
    </Link>
  );
};

type ChatMessage = { role: "user" | "assistant"; text: string };

const AskSeraPanel = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      text: "Hi! I'm Sera. Ask me anything about your calls, bookings, or patients.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [...messages, { role: "user", text }].map((m) => ({
            role: m.role,
            content: m.text,
          })),
          system:
            "You are Sera's AI assistant for clinic staff. Answer questions about calls, bookings, patients, and performance data concisely and helpfully.",
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            text: data.message ?? data.content ?? "Got it.",
          },
        ]);
      } else {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            text: "I couldn't reach the server. Try again.",
          },
        ]);
      }
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "I couldn't reach the server. Try again." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 199,
            background: "transparent",
          }}
        />
      )}
      <div
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100vh",
          width: 360,
          background: "#FFFFFF",
          borderLeft: "1px solid #E8E8E8",
          zIndex: 200,
          display: "flex",
          flexDirection: "column",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: open ? "-4px 0 24px rgba(0,0,0,0.08)" : "none",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #E8E8E8",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600, color: "#111111" }}>
            Ask Sera
          </span>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#999999",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 4,
              borderRadius: 6,
            }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                background: m.role === "user" ? "#000000" : "#F7F7F7",
                color: m.role === "user" ? "#FFFFFF" : "#111111",
                borderRadius:
                  m.role === "user"
                    ? "12px 12px 2px 12px"
                    : "12px 12px 12px 2px",
                padding: "10px 14px",
                fontSize: 14,
                maxWidth: "80%",
                lineHeight: 1.5,
                animation: "fadeIn 0.2s ease",
              }}
            >
              {m.text}
            </div>
          ))}
          {sending && (
            <div
              style={{
                alignSelf: "flex-start",
                background: "#F7F7F7",
                borderRadius: "12px 12px 12px 2px",
                padding: "10px 14px",
                display: "flex",
                gap: 4,
                alignItems: "center",
              }}
            >
              <Loader2
                style={{
                  width: 14,
                  height: 14,
                  color: "#999",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <span style={{ fontSize: 13, color: "#999" }}>Thinking…</span>
            </div>
          )}
        </div>

        {/* Input */}
        <div
          style={{
            borderTop: "1px solid #E8E8E8",
            padding: 16,
            display: "flex",
            gap: 8,
          }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask anything…"
            rows={1}
            style={{
              flex: 1,
              border: "1px solid #E8E8E8",
              borderRadius: 8,
              padding: "9px 12px",
              fontSize: 14,
              fontFamily: "Inter, sans-serif",
              resize: "none",
              outline: "none",
              color: "#111111",
              background: "#FFFFFF",
              transition: "border-color 0.15s",
            }}
            onFocus={(e) => {
              (e.target as HTMLTextAreaElement).style.borderColor = "#111111";
            }}
            onBlur={(e) => {
              (e.target as HTMLTextAreaElement).style.borderColor = "#E8E8E8";
            }}
          />
          <button
            onClick={send}
            disabled={!input.trim() || sending}
            style={{
              width: 38,
              height: 38,
              background: input.trim() && !sending ? "#000000" : "#E8E8E8",
              border: "none",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: input.trim() && !sending ? "pointer" : "not-allowed",
              flexShrink: 0,
              transition: "background 0.15s",
            }}
          >
            <Send
              style={{
                width: 15,
                height: 15,
                color: input.trim() && !sending ? "#FFFFFF" : "#999999",
              }}
            />
          </button>
        </div>
      </div>
    </>
  );
};

const Header = () => {
  const location = useLocation();
  const { identity } = useGetIdentity();
  const logout = useLogout();
  const [askOpen, setAskOpen] = useState(false);

  const isActive = (pattern: string) => {
    if (pattern === "/") return location.pathname === "/";
    return !!matchPath(pattern, location.pathname);
  };

  const displayName =
    identity?.fullName ?? identity?.email?.split("@")[0] ?? "";
  const initials = displayName
    ? displayName
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "S";

  const NavSection = ({
    label,
    items,
  }: {
    label: string;
    items: NavItem[];
  }) => (
    <div style={{ padding: "8px 0" }}>
      <p className="section-label" style={{ padding: "8px 16px 4px" }}>
        {label}
      </p>
      {items.map((item) => (
        <NavLink key={item.to} item={item} active={isActive(item.pattern)} />
      ))}
    </div>
  );

  return (
    <>
      {/* Sidebar */}
      <aside
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          height: "100vh",
          width: 220,
          background: "#FFFFFF",
          borderRight: "1px solid #E8E8E8",
          display: "flex",
          flexDirection: "column",
          zIndex: 20,
        }}
      >
        {/* Logo */}
        <div style={{ padding: "20px 16px 16px" }}>
          <Link
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              textDecoration: "none",
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                background: "#000000",
                borderRadius: 3,
                flexShrink: 0,
              }}
            />
            <span
              className="font-serif"
              style={{ fontSize: 18, fontWeight: 400, color: "#111111" }}
            >
              Sera
            </span>
          </Link>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: "auto" }}>
          <NavSection label="Manage" items={MANAGE_NAV} />
          <NavSection label="Insights" items={INSIGHTS_NAV} />
          <NavSection label="Settings" items={SETTINGS_NAV} />
        </nav>

        {/* Bottom: logout + Ask Sera */}
        <div style={{ padding: 16 }}>
          <button
            onClick={() => setAskOpen(true)}
            style={{
              width: "100%",
              background: "#000000",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 999,
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 500,
              fontFamily: "Inter, sans-serif",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "background 0.15s",
              marginBottom: 8,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#222222";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#000000";
            }}
          >
            <span style={{ fontSize: 13 }}>✦</span> Ask anything
          </button>

          <button
            onClick={() => logout()}
            style={{
              width: "100%",
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 16px",
              borderRadius: 8,
              fontSize: 14,
              color: "#999999",
              fontFamily: "Inter, sans-serif",
              transition: "color 0.12s, background 0.12s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#FEF2F2";
              (e.currentTarget as HTMLElement).style.color = "#DC2626";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "none";
              (e.currentTarget as HTMLElement).style.color = "#999999";
            }}
          >
            <LogOut style={{ width: 16, height: 16 }} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Top bar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 220,
          right: 0,
          height: 56,
          background: "#FFFFFF",
          borderBottom: "1px solid #E8E8E8",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 32px",
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 500, color: "#111111" }}>
          Good{" "}
          {new Date().getHours() < 12
            ? "morning"
            : new Date().getHours() < 17
              ? "afternoon"
              : "evening"}
          {displayName ? `, ${displayName}` : ""}
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            title="Notifications"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#999999",
              display: "flex",
              alignItems: "center",
              padding: 4,
              borderRadius: 6,
            }}
          >
            <Bell style={{ width: 20, height: 20 }} />
          </button>
          <Link
            to="/settings"
            title="Settings"
            style={{
              color: "#999999",
              display: "flex",
              alignItems: "center",
              padding: 4,
              borderRadius: 6,
            }}
          >
            <Settings style={{ width: 20, height: 20 }} />
          </Link>
          <div
            title={displayName}
            style={{
              width: 32,
              height: 32,
              background: "#000000",
              color: "#FFFFFF",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 600,
              cursor: "default",
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
        </div>
      </div>

      <AskSeraPanel open={askOpen} onClose={() => setAskOpen(false)} />
    </>
  );
};

export default Header;
